// role-based-dashboards — Phase 6.2: ungated recent audit-log feed widget.
//
// Compact last-5 audit events, deep-linking to the full AuditLogPage
// (spec "recent audit-log feed (deep-link /dashboard/audit-log)") rather
// than re-implementing filtering/pagination/detail-drilldown here.

import { listAuditLog } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import { Button, Card, CardContent, Skeleton } from '@jq4race/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { resolveSummary } from '../../../audit-log/lib/resolveSummary';

const FEED_LIMIT = 5;

function formatTimestamp(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function RecentAuditFeed() {
  const { t, i18n } = useTranslation('dashboard');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard-overview', 'recent-audit-log'],
    queryFn: () => listAuditLog({ limit: FEED_LIMIT }),
  });

  return (
    <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center justify-between">
          <p className="jq-eyebrow">{t('recentActivity.eyebrow')}</p>
          <Button asChild variant="ghost" size="sm" className="text-[13px]">
            <Link to="/dashboard/audit-log">{t('recentActivity.viewAll')}</Link>
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton rows
              <Skeleton key={`audit-skel-${i}`} className="h-5 w-full" />
            ))}
          </div>
        ) : isError ? (
          <p className="flex items-center gap-2 text-[13px] text-jq-danger">
            <TriangleAlert className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            {translateApiError(error, t, { fallback: t('recentActivity.error') })}
          </p>
        ) : data && data.items.length > 0 ? (
          <ul className="divide-y divide-jq-sand/40">
            {data.items.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-3 py-2 text-[13px]"
              >
                <span className="truncate text-jq-charcoal">{resolveSummary(entry, t)}</span>
                <span className="shrink-0 font-mono text-[11px] text-jq-graphite">
                  {formatTimestamp(entry.createdAt, i18n.language)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-4 text-center text-[13px] text-jq-stone">{t('recentActivity.empty')}</p>
        )}
      </CardContent>
    </Card>
  );
}
